/**
 * SpecializationsDialog - Manage teacher specializations
 *
 * Allows adding/removing teacher specializations for:
 * - Dance styles (from existing styles in the studio)
 * - Age groups (predefined ranges)
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
  CircularProgress,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import {
  useGetTeacherSpecializationsQuery,
  useCreateTeacherSpecializationMutation,
  useDeleteTeacherSpecializationMutation,
  useGetStylesQuery,
} from '../../../api/schedulingApi';
import type { TeacherSpecialization } from '../../../types/scheduling';

interface SpecializationsDialogProps {
  open: boolean;
  onClose: () => void;
  teacherId: string;
  teacherName: string;
  studioId: string;
}

const SpecializationsDialog: React.FC<SpecializationsDialogProps> = ({
  open,
  onClose,
  teacherId,
  teacherName,
  studioId,
}) => {
  // Fetch specializations
  const {
    data: specializations,
    isLoading,
    error,
  } = useGetTeacherSpecializationsQuery(
    { teacherId, studioId },
    { skip: !teacherId || !studioId }
  );

  // Fetch available styles for the studio
  const { data: styles, isLoading: isLoadingStyles } = useGetStylesQuery({
    studioId,
  });

  const [createSpecialization, { isLoading: isCreating }] =
    useCreateTeacherSpecializationMutation();
  const [deleteSpecialization, { isLoading: isDeleting }] =
    useDeleteTeacherSpecializationMutation();

  // Form state
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [specializationType, setSpecializationType] = useState<
    'style' | 'age_group'
  >('style');
  const [specializationValue, setSpecializationValue] = useState<string>('');
  const [minAge, setMinAge] = useState<number>(3);
  const [maxAge, setMaxAge] = useState<number>(18);
  const [formError, setFormError] = useState<string>('');

  const resetForm = () => {
    setSpecializationType('style');
    setSpecializationValue('');
    setMinAge(3);
    setMaxAge(18);
    setFormError('');
  };

  const handleOpenAddForm = () => {
    resetForm();
    setIsAddFormOpen(true);
  };

  const handleCloseAddForm = () => {
    setIsAddFormOpen(false);
    resetForm();
  };

  const handleAddSpecialization = async () => {
    let valueToUse = specializationValue;

    // For age groups, create the value from min/max
    if (specializationType === 'age_group') {
      if (minAge < 0 || maxAge < 0) {
        setFormError('Ages must be positive numbers');
        return;
      }
      if (minAge > maxAge) {
        setFormError('Minimum age must be less than or equal to maximum age');
        return;
      }
      valueToUse = `${minAge}-${maxAge}`;
    } else if (!specializationValue) {
      setFormError('Please select a style');
      return;
    }

    // Check for duplicates
    const isDuplicate = specializations?.some(
      spec =>
        spec.specialization_type === specializationType &&
        spec.specialization_value === valueToUse
    );

    if (isDuplicate) {
      setFormError('This specialization already exists');
      return;
    }

    try {
      await createSpecialization({
        studioId,
        data: {
          teacher: teacherId,
          specialization_type: specializationType,
          specialization_value: valueToUse,
        },
      }).unwrap();

      handleCloseAddForm();
    } catch (err) {
      console.error('Failed to create specialization:', err);
      setFormError('Failed to create specialization');
    }
  };

  const handleDelete = async (spec: TeacherSpecialization) => {
    try {
      await deleteSpecialization({
        id: spec.id,
        studioId,
        teacherId,
      }).unwrap();
    } catch (err) {
      console.error('Failed to delete specialization:', err);
    }
  };

  // Group specializations by type
  const styleSpecializations = specializations?.filter(
    spec => spec.specialization_type === 'style'
  );
  const ageGroupSpecializations = specializations?.filter(
    spec => spec.specialization_type === 'age_group'
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {teacherName} Specializations
        <Typography variant="caption" display="block" color="text.secondary">
          Define which styles and age groups this teacher specializes in
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Current Specializations Display */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Specializations
            </Typography>

            {isLoading && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!isLoading && error && (
              <Alert severity="error">Failed to load specializations</Alert>
            )}
            {!isLoading &&
              !error &&
              (!specializations || specializations.length === 0) && (
                <Alert severity="info">
                  No specializations set. Add specializations below to help the
                  algorithm match this teacher with appropriate classes.
                </Alert>
              )}
            {!isLoading &&
              !error &&
              specializations &&
              specializations.length > 0 && (
                <List>
                  {/* Dance Styles Section */}
                  {styleSpecializations && styleSpecializations.length > 0 && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <SchoolIcon fontSize="small" color="primary" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Dance Styles
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {styleSpecializations.map(spec => (
                        <ListItem
                          key={spec.id}
                          secondaryAction={
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(spec)}
                              disabled={isDeleting}
                              title="Remove"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{ pl: 4 }}
                        >
                          <Chip
                            label={spec.specialization_value}
                            color="primary"
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </>
                  )}

                  {/* Age Groups Section */}
                  {ageGroupSpecializations &&
                    ageGroupSpecializations.length > 0 && (
                      <>
                        {styleSpecializations &&
                          styleSpecializations.length > 0 && (
                            <Divider sx={{ my: 1 }} />
                          )}
                        <ListItem>
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <PeopleIcon
                                  fontSize="small"
                                  color="secondary"
                                />
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  Age Groups
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                        {ageGroupSpecializations.map(spec => (
                          <ListItem
                            key={spec.id}
                            secondaryAction={
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(spec)}
                                disabled={isDeleting}
                                title="Remove"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            }
                            sx={{ pl: 4 }}
                          >
                            <Chip
                              label={spec.specialization_value}
                              color="secondary"
                              size="small"
                            />
                          </ListItem>
                        ))}
                      </>
                    )}
                </List>
              )}
          </Box>

          <Divider />

          {/* Add Form */}
          {isAddFormOpen ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Add New Specialization
              </Typography>
              <Stack spacing={2}>
                {formError && <Alert severity="error">{formError}</Alert>}

                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={specializationType}
                    onChange={e => {
                      setSpecializationType(
                        e.target.value as 'style' | 'age_group'
                      );
                      setSpecializationValue(''); // Reset value when type changes
                      setFormError('');
                    }}
                    label="Type"
                  >
                    <MenuItem value="style">Dance Style</MenuItem>
                    <MenuItem value="age_group">Age Group</MenuItem>
                  </Select>
                </FormControl>

                {specializationType === 'style' ? (
                  <FormControl fullWidth>
                    <InputLabel>Style</InputLabel>
                    <Select
                      value={specializationValue}
                      onChange={e => setSpecializationValue(e.target.value)}
                      label="Style"
                      disabled={
                        isLoadingStyles || !styles || styles.length === 0
                      }
                    >
                      {styles && styles.length > 0 ? (
                        styles.map(style => (
                          <MenuItem key={style.id} value={style.title}>
                            {style.title}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No styles available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                ) : (
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Minimum Age"
                      value={minAge}
                      onChange={e =>
                        setMinAge(parseInt(e.target.value, 10) || 0)
                      }
                      inputProps={{ min: 0, max: 100 }}
                      helperText="Youngest age this teacher can teach"
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Maximum Age"
                      value={maxAge}
                      onChange={e =>
                        setMaxAge(parseInt(e.target.value, 10) || 0)
                      }
                      inputProps={{ min: 0, max: 100 }}
                      helperText="Oldest age this teacher can teach"
                    />
                  </Stack>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSpecialization}
                    disabled={isCreating}
                  >
                    Add Specialization
                  </Button>
                  <Button onClick={handleCloseAddForm}>Cancel</Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddForm}
            >
              Add Specialization
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecializationsDialog;
